import * as z from 'zod';

export const ListSitemapPagesRequest = z.object({
  options: z
    .object({
      itemType: z
        .enum([
          'STORES_PRODUCT',
          'BLOG_POST',
          'BLOG_CATEGORY',
          'BLOG_TAGS',
          'BLOG_ARCHIVE',
          'FORUM_POST',
          'FORUM_CATEGORY',
          'GROUPS_POST',
          'GROUPS_PAGE',
          'EVENTS_PAGE',
          'BOOKINGS_SERVICE',
          'MEMBERS_AREA_PROFILE',
          'CHALLENGES_PAGE',
          'STORES_CATEGORY',
          'STORES_SUB_CATEGORY',
          'PORTFOLIO_COLLECTIONS',
          'PORTFOLIO_PROJECTS',
          'PRICING_PLANS',
          'RESTAURANTS_MENU_PAGE',
        ])
        .optional(),
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Maximum number of items to return in the results.')
            .min(0)
            .max(200)
            .optional()
            .nullable(),
          cursor: z
            .string()
            .describe(
              "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe('Optional cursor for pagination')
        .optional(),
    })
    .optional(),
});
export const ListSitemapPagesResponse = z.object({
  pages: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Entity identifier (e.g., productId, pageId)')
          .min(1)
          .max(255)
          .optional(),
        shouldBeIncludedInSitemap: z
          .boolean()
          .describe(
            'Indicates if the entry should be included in the generated sitemap.'
          )
          .optional(),
        _updatedDate: z
          .string()
          .describe('Optional last modification date in ISO 8601 format')
          .max(50)
          .optional(),
        images: z
          .array(
            z.object({
              url: z
                .string()
                .describe('Image URL')
                .url()
                .max(2048)
                .min(1)
                .optional(),
              altText: z
                .string()
                .describe('Optional image alt text')
                .max(2048)
                .optional(),
            })
          )
          .max(1000)
          .optional(),
        videos: z
          .array(
            z.object({
              url: z
                .string()
                .describe('Video content location URL')
                .url()
                .max(2048)
                .min(1)
                .optional(),
              thumbnailUrl: z
                .string()
                .describe('Video thumbnail location URL')
                .url()
                .max(2048)
                .min(1)
                .optional(),
              title: z
                .string()
                .describe('Optional video title')
                .max(255)
                .optional(),
              description: z
                .string()
                .describe('Optional video description')
                .max(1000)
                .optional(),
            })
          )
          .max(100)
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Paging metadata for next page of results')
    .optional(),
});
